/**
 * Spend Easy - Receipt Parser & OCR Intelligence Engine
 * Extracts merchant names, totals, purchase dates, and predicts expense categories.
 */

// Known merchant signatures and their default categories
export const KNOWN_MERCHANTS = [
  // Food & Dining
  { name: 'Starbucks', category: 'Food & Dining', patterns: [/starbucks/i, /sbux/i] },
  { name: "McDonald's", category: 'Food & Dining', patterns: [/mcdonald/i, /mc donald/i] },
  { name: 'Chipotle Mexican Grill', category: 'Food & Dining', patterns: [/chipotle/i] },
  { name: 'Subway', category: 'Food & Dining', patterns: [/subway/i] },
  { name: 'Domino\'s Pizza', category: 'Food & Dining', patterns: [/domino/i] },
  { name: 'Dunkin\'', category: 'Food & Dining', patterns: [/dunkin/i, /dd\s*donut/i] },
  { name: 'Burger King', category: 'Food & Dining', patterns: [/burger\s*king/i] },
  { name: 'KFC', category: 'Food & Dining', patterns: [/kfc/i, /kentucky\s*fried/i] },
  { name: 'Panera Bread', category: 'Food & Dining', patterns: [/panera/i] },
  { name: 'Blue Bottle Coffee', category: 'Food & Dining', patterns: [/blue\s*bottle/i] },

  // Groceries
  { name: 'Whole Foods Market', category: 'Groceries', patterns: [/whole\s*foods/i, /wfm/i] },
  { name: "Trader Joe's", category: 'Groceries', patterns: [/trader\s*joe/i] },
  { name: 'Walmart', category: 'Groceries', patterns: [/walmart/i, /wal-mart/i] },
  { name: 'Target', category: 'Groceries', patterns: [/target/i] },
  { name: 'Costco Wholesale', category: 'Groceries', patterns: [/costco/i] },
  { name: 'Kroger', category: 'Groceries', patterns: [/kroger/i] },
  { name: 'Safeway', category: 'Groceries', patterns: [/safeway/i] },
  { name: 'Aldi', category: 'Groceries', patterns: [/aldi/i] },
  { name: 'Instacart', category: 'Groceries', patterns: [/instacart/i] },
  { name: 'BigBasket / Blinkit', category: 'Groceries', patterns: [/blinkit/i, /bigbasket/i, /zepto/i] },

  // Transportation
  { name: 'Shell Oil', category: 'Transportation', patterns: [/shell/i] },
  { name: 'Chevron', category: 'Transportation', patterns: [/chevron/i] },
  { name: 'BP Gas', category: 'Transportation', patterns: [/bp\s*connect/i, /bp\s*oil/i, /\bbp\b/i] },
  { name: 'ExxonMobil', category: 'Transportation', patterns: [/exxon/i, /mobil/i] },
  { name: 'Uber Technologies', category: 'Transportation', patterns: [/uber\s*trip/i, /uber\b/i, /uber\s*eats/i] },
  { name: 'Lyft', category: 'Transportation', patterns: [/lyft/i] },
  { name: 'Indian Oil / HP', category: 'Transportation', patterns: [/indianoil/i, /hindustan\s*petroleum/i, /bharat\s*petroleum/i] },

  // Shopping & Electronics
  { name: 'Amazon', category: 'Shopping', patterns: [/amazon/i, /amzn/i] },
  { name: 'Apple Store', category: 'Shopping', patterns: [/apple\s*store/i, /apple\.com/i] },
  { name: 'Best Buy', category: 'Shopping', patterns: [/best\s*buy/i] },
  { name: 'Zara', category: 'Shopping', patterns: [/zara/i] },
  { name: 'H&M', category: 'Shopping', patterns: [/h&m/i, /hennes/i] },
  { name: 'Nike', category: 'Shopping', patterns: [/nike/i] },
  { name: 'Home Depot', category: 'Shopping', patterns: [/home\s*depot/i] },
  { name: 'IKEA', category: 'Shopping', patterns: [/ikea/i] },

  // Healthcare
  { name: 'CVS Pharmacy', category: 'Healthcare', patterns: [/cvs/i] },
  { name: 'Walgreens', category: 'Healthcare', patterns: [/walgreens/i] },
  { name: 'Rite Aid', category: 'Healthcare', patterns: [/rite\s*aid/i] },
  { name: 'Apollo Pharmacy', category: 'Healthcare', patterns: [/apollo\s*pharmacy/i] },

  // Entertainment
  { name: 'Netflix', category: 'Entertainment', patterns: [/netflix/i] },
  { name: 'Spotify', category: 'Entertainment', patterns: [/spotify/i] },
  { name: 'AMC Theatres', category: 'Entertainment', patterns: [/amc\s*theatre/i, /amc/i] },
  { name: 'Regal Cinemas', category: 'Entertainment', patterns: [/regal/i] },

  // Housing & Utilities
  { name: 'Electric / Utility', category: 'Housing & Utilities', patterns: [/electric/i, /power/i, /utility/i, /water/i] },
  { name: 'Internet / Telecom', category: 'Housing & Utilities', patterns: [/comcast/i, /xfinity/i, /at&t/i, /verizon/i, /t-mobile/i, /airtel/i, /jio/i] },
];

/**
 * Sample realistic receipts for instant testing without needing paper receipts
 */
export const SAMPLE_RECEIPTS = [
  {
    id: 'sample-starbucks',
    title: 'Starbucks Coffee',
    category: 'Food & Dining',
    storeName: 'Starbucks Coffee #1042',
    date: new Date().toISOString().split('T')[0],
    total: 14.85,
    currency: '$',
    items: [
      { name: 'Caffe Latte (Grande)', price: 5.45 },
      { name: 'Iced Caramel Macchiato', price: 5.95 },
      { name: 'Blueberry Muffin', price: 3.45 }
    ],
    ocrText: `
STARBUCKS STORE #1042
750 MARKET STREET, SAN FRANCISCO, CA
TEL: (415) 555-0199

CHK 482910              REG 02
DATE: ${new Date().toLocaleDateString()}  10:14 AM

1 GR CAFFE LATTE              $5.45
1 ICED CARAMEL MACCHIATO      $5.95
1 BLUEBERRY MUFFIN            $3.45
-----------------------------------
SUBTOTAL                     $14.85
TAX (8.5%)                    $1.26
TOTAL                        $16.11
-----------------------------------
PAYMENT: VISA ****4291
AUTH CODE: 094812
THANK YOU FOR VISITING STARBUCKS!
    `.trim()
  },
  {
    id: 'sample-trader-joes',
    title: "Trader Joe's Groceries",
    category: 'Groceries',
    storeName: "Trader Joe's #214",
    date: new Date().toISOString().split('T')[0],
    total: 48.70,
    currency: '$',
    items: [
      { name: 'Organic Bananas', price: 1.99 },
      { name: 'Almond Butter Crunchy', price: 6.99 },
      { name: 'Organic Sourdough Bread', price: 4.49 },
      { name: 'Greek Yogurt Plain', price: 5.99 },
      { name: 'Cold Brew Coffee Concentrate', price: 8.99 },
      { name: 'Dark Chocolate Almonds', price: 5.49 },
      { name: 'Sparkling Mineral Water (Pack)', price: 4.99 },
      { name: 'Organic Baby Spinach', price: 2.99 },
      { name: 'Mandarin Oranges', price: 4.79 }
    ],
    ocrText: `
TRADER JOE'S
STORE #214 - SUNNYVALE, CA
PHONE: (408) 555-0144

RECEIPT #98102
TRANSACTION DATE: ${new Date().toLocaleDateString()}

ORGANIC BANANAS            $1.99
ALMOND BUTTER CRUNCHY      $6.99
ORGANIC SOURDOUGH BREAD    $4.49
GREEK YOGURT PLAIN         $5.99
COLD BREW COFFEE CONC      $8.99
DARK CHOC ALMONDS          $5.49
SPARKLING WATER 6PK        $4.99
ORGANIC BABY SPINACH       $2.99
MANDARIN ORANGES           $4.79
-----------------------------------
SUBTOTAL                  $46.72
SALES TAX                  $1.98
TOTAL AMOUNT              $48.70
-----------------------------------
MASTERCARD ENDING IN 8821
APPROVED - AUTH 39182
ITEMS SOLD: 9
THANK YOU FOR SHOPPING AT TRADER JOE'S
    `.trim()
  },
  {
    id: 'sample-shell-fuel',
    title: 'Shell Gas Station',
    category: 'Transportation',
    storeName: 'Shell Oil Express #409',
    date: new Date().toISOString().split('T')[0],
    total: 55.40,
    currency: '$',
    items: [
      { name: 'V-Power Regular Gas (12.31 Gal @ 4.50/Gal)', price: 55.40 }
    ],
    ocrText: `
SHELL OIL EXPRESS #409
HIGHWAY 101 NORTH
SAN JOSE, CA

PUMP 04
PRODUCT: REGULAR UNLEADED
GALLONS: 12.311
PRICE/GAL: $4.500
FUEL TOTAL:               $55.40
-----------------------------------
TOTAL SALE:               $55.40
-----------------------------------
CREDIT CARD: VISA
CARD NUMBER: ************1104
INVOICE: 409182
DATE: ${new Date().toLocaleDateString()} 08:42 AM
HAVE A SAFE JOURNEY!
    `.trim()
  },
  {
    id: 'sample-cvs',
    title: 'CVS Pharmacy',
    category: 'Healthcare',
    storeName: 'CVS Pharmacy #882',
    date: new Date().toISOString().split('T')[0],
    total: 24.50,
    currency: '$',
    items: [
      { name: 'Multi-Symptom Cold & Flu', price: 14.99 },
      { name: 'Vitamin C 1000mg 60ct', price: 9.51 }
    ],
    ocrText: `
CVS/pharmacy #882
STORE: 8820 UNIVERSITY AVE
TEL: (650) 555-0177

EXTRA CARE CARD: *******4920
DATE: ${new Date().toLocaleDateString()}

CVS MULTI-SYMPTOM COLD    $14.99
NATURE MADE VITAMIN C      $9.51
-----------------------------------
SUBTOTAL                  $24.50
TAX                        $0.00
BALANCE DUE               $24.50
-----------------------------------
APPLE PAY VISA ****9012
CVS SAVINGS TODAY: $4.00
    `.trim()
  }
];

/**
 * Extracts total amount from text using currency & total patterns
 */
export function extractAmount(text) {
  if (!text) return null;

  // Priority 1: Explicit total keywords followed by amount
  const totalRegexes = [
    /(?:total\s*sale|total\s*amount|balance\s*due|amount\s*due|grand\s*total|net\s*amount|total)[:\s]*([$€£₹¥]?)\s*([0-9,]+\.[0-9]{2})/i,
    /([$€£₹¥]?)\s*([0-9,]+\.[0-9]{2})\s*(?:total|balance\s*due)/i,
    /(?:subtotal|sub-total)[:\s]*([$€£₹¥]?)\s*([0-9,]+\.[0-9]{2})/i,
  ];

  for (const regex of totalRegexes) {
    const match = text.match(regex);
    if (match) {
      const numStr = match[2] || match[1];
      const parsed = parseFloat(numStr.replace(/,/g, ''));
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }

  // Priority 2: Find all currency amounts in text, pick the highest reasonable amount (often the total)
  const allAmounts = [];
  const currencyAmountRegex = /[$€£₹¥]\s*([0-9,]+\.[0-9]{2})/g;
  let match;
  while ((match = currencyAmountRegex.exec(text)) !== null) {
    const val = parseFloat(match[1].replace(/,/g, ''));
    if (!isNaN(val) && val > 0 && val < 50000) {
      allAmounts.push(val);
    }
  }

  if (allAmounts.length > 0) {
    // Return max amount found (standard for receipts where total is >= items)
    return Math.max(...allAmounts);
  }

  // Priority 3: Fallback any decimal amount with 2 decimal places
  const decimalRegex = /\b([0-9]{1,4}\.[0-9]{2})\b/g;
  const genericAmounts = [];
  while ((match = decimalRegex.exec(text)) !== null) {
    const val = parseFloat(match[1]);
    if (!isNaN(val) && val > 0 && val < 50000) {
      genericAmounts.push(val);
    }
  }

  if (genericAmounts.length > 0) {
    return Math.max(...genericAmounts);
  }

  return null;
}

/**
 * Extracts purchase date from text
 */
export function extractDate(text) {
  if (!text) return null;

  // Patterns for YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY, etc.
  const datePatterns = [
    /\b(20[2-3][0-9])[-/.](0[1-9]|1[0-2])[-/.](0[1-9]|[12][0-9]|3[01])\b/, // YYYY-MM-DD
    /\b(0[1-9]|1[0-2])[-/.](0[1-9]|[12][0-9]|3[01])[-/.](20[2-3][0-9]|[2-3][0-9])\b/, // MM/DD/YYYY
    /\b(0[1-9]|[12][0-9]|3[01])[-/.](0[1-9]|1[0-2])[-/.](20[2-3][0-9]|[2-3][0-9])\b/, // DD/MM/YYYY
    /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+([0-9]{1,2}),?\s+(20[2-3][0-9])\b/i, // Month DD, YYYY
  ];

  for (const regex of datePatterns) {
    const match = text.match(regex);
    if (match) {
      try {
        const parsed = new Date(match[0]);
        if (!isNaN(parsed.getTime())) {
          return parsed.toISOString().split('T')[0];
        }
      } catch (e) {}
    }
  }

  return new Date().toISOString().split('T')[0];
}

/**
 * Extracts or predicts the merchant name
 */
export function extractMerchant(text) {
  if (!text) return 'Receipt Purchase';

  // Check known merchants
  for (const item of KNOWN_MERCHANTS) {
    for (const pat of item.patterns) {
      if (pat.test(text)) {
        return item.name;
      }
    }
  }

  // Otherwise, use the first non-empty alphanumeric line of the receipt
  const lines = text
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 2 && /[a-zA-Z]/.test(l) && !/^(date|time|store|tel|phone|cashier|reg|chk|#)/i.test(l));

  if (lines.length > 0) {
    // Clean up line
    const firstLine = lines[0].replace(/[^a-zA-Z0-9\s&'-]/g, '').trim();
    if (firstLine.length > 2 && firstLine.length < 35) {
      return firstLine;
    }
  }

  return 'Retail Purchase';
}

/**
 * Predicts category based on merchant and line keywords
 */
export function predictCategory(merchant, text) {
  const combined = `${merchant || ''} ${text || ''}`.toLowerCase();

  // Known merchant category match
  for (const item of KNOWN_MERCHANTS) {
    if (item.name.toLowerCase() === (merchant || '').toLowerCase()) {
      return item.category;
    }
    for (const pat of item.patterns) {
      if (pat.test(combined)) {
        return item.category;
      }
    }
  }

  // Keyword rules
  if (/coffee|latte|cafe|burger|pizza|sandwich|bistro|diner|bar|grill|bakery|restaurant|donut|taco/i.test(combined)) {
    return 'Food & Dining';
  }
  if (/market|grocery|supermarket|produce|fruit|bread|milk|eggs|yogurt|butter|cheese/i.test(combined)) {
    return 'Groceries';
  }
  if (/gas|fuel|petrol|oil|pump|diesel|parking|transit|metro|toll|cab|taxi/i.test(combined)) {
    return 'Transportation';
  }
  if (/pharmacy|rx|drug|medicine|pill|clinic|health|doctor|dental/i.test(combined)) {
    return 'Healthcare';
  }
  if (/theater|cinema|movie|show|concert|ticket|game|arcade/i.test(combined)) {
    return 'Entertainment';
  }
  if (/power|electric|utility|water|gas\s*bill|broadband|wifi|internet/i.test(combined)) {
    return 'Housing & Utilities';
  }
  if (/clothing|shoes|shirt|pants|dress|jacket|apparel|electronics|gadget/i.test(combined)) {
    return 'Shopping';
  }

  return 'Shopping';
}

/**
 * Parses full raw OCR text into structured transaction fields
 */
export function parseReceiptText(ocrText) {
  const merchant = extractMerchant(ocrText);
  const amount = extractAmount(ocrText) || 0;
  const date = extractDate(ocrText);
  const category = predictCategory(merchant, ocrText);

  const confidence = (amount > 0 ? 0.45 : 0) + 
                     (merchant !== 'Retail Purchase' && merchant !== 'Receipt Purchase' ? 0.35 : 0.1) +
                     (date ? 0.2 : 0);

  return {
    merchant,
    amount,
    date,
    category,
    confidence: Math.round(confidence * 100),
    rawText: ocrText
  };
}
